/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { updateAllLabels } from "../../api/Label";
import { LabelAtom } from "../../atoms/LabelAtom";
import { ILabel } from "../../types/LabelTypes";
import { useModal } from "../Modal/Modal";
import StyledButton from "../StyledButton";
import StyledInput from "../StyledInput";
import Labels from "./Labels";

const ModalBody = styled.div`
  max-width: 394px;
  min-width: 394px;
  max-height: 360px;
  min-height: 240px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  border-top: 1px solid #eceeee;

  padding: 24px;
  margin: 18px -22px -25px;

  > div + div {
    margin-top: 16px;
  }
`;

const Buttons = styled.span`
  width: 100%;

  margin-top: 32px;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  border-top: 1px solid #eceeee;

  padding: 20px 0 0;
`;

const InputLabel = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  > input::placeholder {
    font-size: 16px;
    color: #979797;
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2f2f2f;

  margin-bottom: 16px;
`;

interface Props {
  labelIndex: number;
  labels: ILabel[];
}

export default function EditLabelModal(props: Props): JSX.Element {
  const { labelIndex } = props;
  const modalHandler = useModal();
  const [labels, setLabelsArray] = useState(props.labels);

  const [updatedLabel, setUpdatedLabel] = useState<ILabel>();
  const [, setLabels] = useRecoilState(LabelAtom);
  const [labelColorsNotAvailable, setLabelColorsNotAvailable] = useState<
    string[]
  >([]);

  useEffect(() => {
    setLabelsArray(props.labels);
  }, [props.labels]);

  useEffect(() => {
    if (labels && updatedLabel) {
      setLabelColorsNotAvailable(
        labels.filter((l) => l._id !== updatedLabel._id).map((lab) => lab.color)
      );
    }
  }, [labels, updatedLabel]);

  useEffect(() => {
    if (labels) {
      const label = labels[labelIndex];

      setUpdatedLabel(label);
    }
  }, [labelIndex, labels]);

  function handleChangeNameLabel(value: string) {
    // Check only for TS
    if (updatedLabel) {
      setUpdatedLabel({
        ...updatedLabel,
        name: value,
      });
    }
  }

  function handleLabelChecked(value: string) {
    // Check only for TS
    if (updatedLabel) {
      setUpdatedLabel({
        ...updatedLabel,
        color: value,
      });
    }
  }

  function handleSaveButton() {
    // Check only for TS
    if (labels)
      if (updatedLabel?.color && updatedLabel?.name.trim()) {
        const updatedLabelArray = [...labels];

        updatedLabelArray.splice(labelIndex, 1, updatedLabel);

        updateAllLabels(updatedLabelArray);
        setLabels(updatedLabelArray);
        modalHandler.popModal();
      } else {
        toast("Name and color are required", { type: "error" });
      }
  }

  return (
    <ModalBody>
      <InputLabel>
        <Title>Label Name</Title>
        <StyledInput
          placeholder="Enter label name"
          value={updatedLabel?.name}
          onChange={(evt) => handleChangeNameLabel(evt.target.value)}
        />
      </InputLabel>
      <InputLabel>
        <Title>Label Color</Title>
        <Labels
          labelColorsNotAvailable={labelColorsNotAvailable}
          labelChecked={updatedLabel?.color}
          checkedLabelIndex={labelIndex}
          handleLabelChecked={handleLabelChecked}
        />
      </InputLabel>

      <Buttons>
        <StyledButton
          onClick={() => modalHandler.popModal()}
          inverted
          css={css`
            width: 80px;
            margin-right: 10px;
          `}
        >
          Cancel
        </StyledButton>
        <StyledButton
          onClick={handleSaveButton}
          css={css`
            width: 80px;
          `}
        >
          Save
        </StyledButton>
      </Buttons>
    </ModalBody>
  );
}
